import { useCallback, useRef, useState } from 'react';

import { reportUnhandledError } from '@/lib/errors/unhandledErrors';

export type IncidentCreationStatus = 'idle' | 'pending' | 'failed';

type CreateIncidentRequest = {
  key: string;
  requestedIncidentId?: string;
  execute: (incidentId: string) => Promise<void>;
};

export function useCreateIncident() {
  const inProgress = useRef(false);
  const requestedIds = useRef(new Map<string, string>());
  const [statuses, setStatuses] = useState<ReadonlyMap<string, IncidentCreationStatus>>(new Map());

  const create = useCallback(
    async ({ key, requestedIncidentId, execute }: CreateIncidentRequest): Promise<string | null> => {
      if (inProgress.current) return null;
      inProgress.current = true;
      const incidentId = requestedIds.current.get(key) ?? requestedIncidentId ?? crypto.randomUUID();
      requestedIds.current.set(key, incidentId);
      setStatuses((current) => new Map(current).set(key, 'pending'));
      try {
        await execute(incidentId);
        setStatuses((current) => new Map(current).set(key, 'idle'));
        return incidentId;
      } catch (error) {
        setStatuses((current) => new Map(current).set(key, 'failed'));
        reportUnhandledError(error);
        return null;
      } finally {
        inProgress.current = false;
      }
    },
    [],
  );

  const reset = useCallback(() => {
    if (inProgress.current) return;
    requestedIds.current.clear();
    setStatuses(new Map());
  }, []);

  const isPending = useCallback(() => inProgress.current, []);

  return {
    create,
    reset,
    isPending,
    pending: [...statuses.values()].includes('pending'),
    statusFor: (key: string): IncidentCreationStatus => statuses.get(key) ?? 'idle',
  };
}
